import React from 'react';
import { useAuth } from '../context/AuthContext';
import { getRankInfo } from '../utils/rankUtils';
import './Ranks.css';

interface RanksProps {
  onNavigate: (page: string) => void;
}

const Ranks: React.FC<RanksProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  
  const ranks = [
    { key: 'beginner', ...getRankInfo('beginner'), description: 'Just starting your journey', benefits: ['Basic green frame', 'View activity feed', 'Use love & clap reactions'] },
    { key: 'newcomer', ...getRankInfo('newcomer'), description: 'Taking first steps in cleanliness', benefits: ['Blue frame', 'Comment on activities', 'Thumbs down reaction'] },
    { key: 'rookie', ...getRankInfo('rookie'), description: 'Showing commitment', benefits: ['Purple frame', 'Fire emoji reaction', 'Extended bio (100 chars)'] },
    { key: 'novice', ...getRankInfo('novice'), description: 'Learning the ropes', benefits: ['Silver frame', 'Extended bio (200 chars)', 'View detailed stats'] },
    { key: 'cleaner', ...getRankInfo('cleaner'), description: 'Regular contributor to cleanliness', benefits: ['Animated frame', 'Star emoji reaction', 'Create polls'] },
    { key: 'eco-warrior', ...getRankInfo('eco-warrior'), description: 'Dedicated environmental protector', benefits: ['Glowing frame', 'Bold comments', 'Extended bio (300 chars)'] },
    { key: 'green-champion', ...getRankInfo('green-champion'), description: 'Champion of sustainability', benefits: ['Rainbow frame', 'Pin one post', 'Profile themes'] },
    { key: 'campus-hero', ...getRankInfo('campus-hero'), description: 'Hero of the campus', benefits: ['Diamond frame', 'Verified badge', 'Pin two posts'] },
    { key: 'legendary', ...getRankInfo('legendary'), description: 'Legendary status achieved', benefits: ['Golden crown frame', 'Muscle emoji reaction', 'All exclusive features'] }
  ];

  const currentRankIndex = ranks.findIndex(r => r.key === user?.rank);

  return (
    <div className="ranks-page">
      <div className="ranks-header">
        <button onClick={() => onNavigate('dashboard')} className="back-button">
          ← Back
        </button>
        <h1>Rank System</h1>
        <p>Progress through ranks by earning points</p>
      </div>

      <div className="current-rank-display">
        <h2>Your Current Rank</h2>
        <div className="current-rank-card">
          <div className="rank-icon-large">{ranks[currentRankIndex]?.icon}</div>
          <h3>{ranks[currentRankIndex]?.name}</h3>
          <p>{user?.points} points</p>
        </div>
      </div>

      <div className="ranks-list">
        {ranks.map((rank, index) => {
          const isUnlocked = currentRankIndex >= index;
          const isCurrent = currentRankIndex === index;
          const isNext = currentRankIndex + 1 === index;

          return (
            <div 
              key={rank.key} 
              className={`rank-card ${isUnlocked ? 'unlocked' : 'locked'} ${isCurrent ? 'current' : ''} ${isNext ? 'next' : ''}`}
            >
              <div className="rank-header">
                <div className="rank-icon" style={{ fontSize: '2.5rem' }}>
                  {rank.icon}
                </div>
                <div className="rank-info">
                  <h3>{rank.name}</h3>
                  <p className="rank-description">{rank.description}</p>
                  <p className="rank-requirement">
                    {rank.minPoints === 0 ? 'Starting rank' : `Requires ${rank.minPoints} points`}
                  </p>
                </div>
                <div className="rank-status">
                  {isCurrent && <span className="status-badge current">Current</span>}
                  {isNext && <span className="status-badge next">Next</span>}
                  {isUnlocked && !isCurrent && <span className="status-badge unlocked">Unlocked</span>}
                  {!isUnlocked && !isNext && <span className="status-badge locked">Locked</span>}
                </div>
              </div>

              <div className="rank-benefits">
                <h4>Benefits:</h4>
                <ul>
                  {rank.benefits.map((benefit, i) => (
                    <li key={i}>
                      <span className="benefit-icon">✓</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              {isNext && user && (
                <div className="rank-progress">
                  <p>You need <strong>{rank.minPoints - user.points}</strong> more points to reach this rank</p>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${(user.points / rank.minPoints) * 100}%` }}
                    />
                  </div>
                  <span className="progress-text">{Math.round((user.points / rank.minPoints) * 100)}%</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default Ranks;